import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useCreateJobFile } from "@/hooks/mutations/useCreateJobFile";
import { useAddJobFile } from "@/hooks/sets/useAddJobFile";
import { JobFile } from "@/types";

const createJobFileSchema = z.object({
  jobFile: z.instanceof(File, {
    message: "Required",
  }),
  name: z.string().min(1, {
    message: "Required",
  }),
});

export const CreateJobFileDialog = ({
  onOpenChange,
  onSuccess,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data?: JobFile) => void;
  open: boolean;
}) => {
  const { mutate: createJobFile, isPending } = useCreateJobFile();
  const addJobFile = useAddJobFile();

  const form = useForm<z.infer<typeof createJobFileSchema>>({
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(createJobFileSchema),
  });

  function onSubmit(data: z.infer<typeof createJobFileSchema>) {
    createJobFile(
      {
        createJobFileRequest: {
          jobFile: data.jobFile,
          name: data.name.trim(),
        },
      },
      {
        onSuccess: (newJobFile) => {
          form.reset();
          onSuccess?.(newJobFile);
          addJobFile({
            jobFileToAdd: newJobFile,
          });
        },
      },
    );
  }

  return (
    <Dialog
      onOpenChange={(open) => {
        // Reset the form when the dialog is closed.
        form.reset();
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-slate-700">
            Upload File
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Name" {...field} />
                  </FormControl>
                  <FormDescription>
                    The name of the file minus the extension.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="jobFile"
              render={({ field: { onChange, ...field } }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>File</FormLabel>
                  <FormControl>
                    <Input
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        onChange(file ?? undefined);
                      }}
                      type="file"
                      {...field}
                      // QUESTION: Why is this needed?
                      value={undefined}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPending}
                type="submit"
              >
                Upload
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
